/**
 * AI Texts and Decline Messages are two independent per-location switches, each backed by its own
 * MerchantLocation column. They gate different features — `enable_decline_messages` is read by the
 * backend when a payment declines, while `enable_ai_texts` gates the AI Texts opt-in and has no
 * send-side consumer yet.
 */

export const AI_TEXTS_FIELD = 'enable_ai_texts'
export const DECLINE_MESSAGES_FIELD = 'enable_decline_messages'

export type LocationFlagField =
  | typeof AI_TEXTS_FIELD
  | typeof DECLINE_MESSAGES_FIELD

export const LOCATIONS_PAGE_SIZE = 100

/** `OptIn.media` value the per-location switch gates. */
export const AI_TEXTS_MEDIA = 'AI_TEXTS'

/**
 * the location list now comes from core-service in camelCase, while the PATCH that writes this column
 * still goes to Django under its snake_case name. So the read accepts either shape and AI_TEXTS_FIELD
 * stays snake_case for the write. Both are checked because store locations (core) and a Django PATCH
 * response flow through here.
 */
export function isAiTextsEnabled(location: Record<string, any>): boolean {
  return Boolean(location?.enableAiTexts ?? location?.[AI_TEXTS_FIELD])
}

/**
 * AI Texts state indexed by location id, for views that hold an account rather
 * than a location. Built as one object so Vue 2 observes it on assignment.
 */
export function aiTextsByLocation(
  locations: Record<string, any>[]
): Record<string, boolean> {
  return (locations || []).reduce(
    (enabled: Record<string, boolean>, location) => {
      enabled[location?.id] = isAiTextsEnabled(location)

      return enabled
    },
    {}
  )
}

/** `OptIn.media` value carrying ordinary SMS consent. */
export const TEXT_MEDIA = 'TEXT'

/**
 * Whether the group's TEXT opt-in is on. The rendered group is always PAYMENT —
 * AI_CALLS and AI_TEXTS only ever exist under that category — so this reads
 * PAYMENT/TEXT, the row `check_sendable` gates every outbound text on.
 *
 * Fails closed: a group with no TEXT row reads as opted out.
 */
export function isTextOptedIn(optins: Record<string, any>[]): boolean {
  return Boolean(
    (optins || []).find(optin => optin?.media === TEXT_MEDIA)?.status
  )
}

/**
 * TEXT is the master switch and AI_TEXTS is a preference behind it: an AI text
 * cannot go out while TEXT is off, whatever the AI_TEXTS row says. So the AI
 * Texts switch is locked while TEXT is off — and its stored `status` is left
 * alone, so the preference survives and returns when TEXT comes back.
 */
export function isAiTextsGated(
  optin: Record<string, any> | undefined,
  optins: Record<string, any>[]
): boolean {
  return optin?.media === AI_TEXTS_MEDIA && !isTextOptedIn(optins)
}

/**
 * AI Texts is a preference behind TEXT, so it reads as one only when it sits
 * directly under it — not where an alphabetical `media` sort drops it, above
 * Email. Every other row keeps its incoming order; with no TEXT row to follow,
 * the AI_TEXTS row goes last.
 */
export function orderCommunicationOptins(
  optins: Record<string, any>[]
): Record<string, any>[] {
  const rows = (optins || []).filter(optin => optin?.media !== AI_TEXTS_MEDIA)
  const aiTexts = (optins || []).filter(
    optin => optin?.media === AI_TEXTS_MEDIA
  )

  if (aiTexts.length === 0) {
    return rows
  }

  const textIndex = rows.findIndex(optin => optin?.media === TEXT_MEDIA)
  const insertAt = textIndex === -1 ? rows.length : textIndex + 1

  return [...rows.slice(0, insertAt), ...aiTexts, ...rows.slice(insertAt)]
}

/**
 * What the switch renders. Diverges from `optin.status` only for a gated
 * AI_TEXTS row, which shows off while the stored value stays untouched.
 */
export function optinDisplayStatus(
  optin: Record<string, any> | undefined,
  optins: Record<string, any>[]
): boolean {
  return Boolean(optin?.status) && !isAiTextsGated(optin, optins)
}

/**
 * TODO: remove once AI Texts is cleared for production.
 * 1. This file — delete `canEditAiTexts` and `isAiTextsToggleLocked`.
 * 2.
 * 3.
 * 4.
 */
export function canEditAiTexts(): boolean {
  return window.VUE_APP_ENV !== 'blytzpay-prod'
}

/**
 * Whether a location's switch is locked. In production the AI Texts switch is
 * frozen in both directions, for every merchant — there is no per-merchant
 * exemption, so a location already set on cannot be turned off from the UI
 * either. `enable_decline_messages` is never locked: it is an independent column
 * with a live backend consumer that some locations rely on.
 */
export function isAiTextsToggleLocked(field: LocationFlagField): boolean {
  return field === AI_TEXTS_FIELD && !canEditAiTexts()
}

/** Sorted here so the view doesn't depend on the `ordering` query param. */
export function sortLocationsByName(
  locations: Record<string, any>[]
): Record<string, any>[] {
  return [...locations].sort((a, b) =>
    String(a?.name || '').localeCompare(String(b?.name || ''))
  )
}
